import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren, useState } from 'react';

import {
  ABTest,
  Box,
  type ComponentLink,
  Container,
  FormHero,
  Heading,
  Image,
  LogoSection,
  LucidHead,
  ResourceCards,
  Text,
} from '@lucid/components-react';
import { graphql, withPrefix, type HeadFC } from 'gatsby';
import { getI18nData, getLanguage } from '@lucid/utils';
import { mqMin, theme } from '@lucid/theme';
import qs from 'query-string';

import type {
  Drupal_NodeContactForm,
  Drupal_ParagraphCarouselLogo,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphCtaLinkButton,
  Drupal_ParagraphExternalLogo,
  Drupal_ParagraphExternalLogoSection,
  Drupal_ParagraphFormHero,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphResourceCard,
  Drupal_ParagraphResourceCards,
} from 'generated/drupal-graphql';
import type { Lucid } from 'types/types';
import { getEntityFields } from 'utilities/getEntityFields';
import useAlternateMarketoForm from 'components/ab-tests/optional-phone-number/use-alternate-marketo-form';

const PRODUCT_LOGOS = [
  'https://cdn-cashy-static-assets.lucid.co/lucid/logos/lucidchart.svg',
  'https://cdn-cashy-static-assets.lucid.co/lucid/logos/lucidspark.svg',
  'https://cdn-cashy-static-assets.lucid.co/lucid/logos/lucidscale.svg',
];

const ContactForm: FC<PropsWithChildren<Lucid.Template.PageProps>> = ({ data, pageContext, location }) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeContactForm;
  const ctaPath = data.drupal.secondaryNodeReference?.entityUrl?.path || '';
  const { entityTranslations } = getEntityFields(node);
  const { shortLangPrefix, pageType, nid, shouldHideSsr, marketoForm, i18nKey } = pageContext;
  const { languageKey, urlLangPrefix } = getLanguage(shortLangPrefix);
  const i18n = getI18nData(data.i18nData, i18nKey);
  const parsedQueryString = qs.parse(location?.search || '');
  const shouldSkipForm = parsedQueryString.skipForm === 'true';

  const [isContactFormSuccessful, setIsContactFormSuccessful] = useState(false);

  const handleOnContactFormSuccess = () => {
    setIsContactFormSuccessful(true);
  };

  // Descruture Node
  const {
    fieldFormHero,
    fieldCarouselLogos,
    fieldProductSectionHeader,
    fieldProductSectionSubheader,
    fieldResourceCards,
  } = node;

  // Hero Section
  const formHeroSection = fieldFormHero?.entity as Drupal_ParagraphFormHero;
  const formHeroCtaButton = formHeroSection?.fieldCtaButtons?.[0]?.entity as Drupal_ParagraphCtaButton;
  const formHeroSubheaderLink = {
    linkUrl: formHeroSection.fieldSubheaderLink?.url?.path,
    linkText: formHeroSection.fieldSubheaderLink?.title,
  } as ComponentLink;
  const formHeroCta = {
    displayAsButton: true,
    linkText: formHeroCtaButton?.fieldButtonText,
    linkUrl: formHeroCtaButton?.fieldButtonLink,
  } as ComponentLink;
  const marketoFormFields = formHeroSection.fieldMarketoForm?.entity as Drupal_ParagraphMarketoForm;
  const marketoConfirmCta = marketoFormFields?.fieldMarketoConfirmCta?.entity as Drupal_ParagraphCtaLinkButton;

  // Logos
  const logoSection = fieldCarouselLogos?.entity as Drupal_ParagraphCarouselLogo;
  const logoSectionHeader = logoSection?.fieldHeader;
  const logoData = logoSection?.fieldLogosSection?.entity as Drupal_ParagraphExternalLogoSection;
  const logos = logoData?.fieldExternalLogos?.map(
    e => (e?.entity as Drupal_ParagraphExternalLogo)?.fieldLogoSlug,
  ) as Lucid.Logo.Slug[];

  // Resource Cards
  const resourceCardsSection = fieldResourceCards?.entity as Drupal_ParagraphResourceCards;
  const resourceCardsEntity = resourceCardsSection?.fieldResourceCardItems?.map(
    ({ entity }) => entity as Drupal_ParagraphResourceCard,
  );
  const resourceCards = resourceCardsEntity?.map(card => ({
    heading: card.fieldHeader,
    description: card.fieldDescription,
    cta: {
      linkText: card.fieldResourceLink?.title || '',
      linkUrl: card.fieldResourceLink?.url?.path || '',
    },
  }));

  // For AB Test
  const alternateMarketoForm = useAlternateMarketoForm();

  return (
    <Layout
      entityTranslations={entityTranslations}
      nid={nid}
      pageType={pageType}
      shortLangPrefix={shortLangPrefix}
      shouldHideSsr={shouldHideSsr}
    >
      {fieldFormHero && marketoForm && (
        <ABTest
          isAboveTheFold
          branchesToRender={['T_A', 'T_B']}
          testName="FY24Q3_LucidCoContactSalesOptionalPhone"
          willTriggerFire={location.pathname === withPrefix('/contact/contact-sales')}
        >
          <ABTest.Context.Consumer>
            {({ branch }) => (
              <FormHero
                backgroundColor={theme.colors.blue[5]}
                beaconFormType="sales"
                confirmBody={
                  marketoFormFields?.fieldMarketoConfirmBody || i18n('mktg-ct-all-default-form-thankyou-message')
                }
                confirmCta={
                  marketoConfirmCta
                    ? {
                        linkText: marketoConfirmCta.fieldCta?.title || '',
                        linkUrl: marketoConfirmCta.fieldCta?.url?.path || '',
                        isSecondary: marketoConfirmCta.fieldIsSecondary,
                        displayAsButton: marketoConfirmCta.fieldDisplayAsButton,
                      }
                    : { linkUrl: ctaPath, linkText: 'Learn more' }
                }
                confirmHeading={
                  marketoFormFields?.fieldMarketoConfirmHeading || i18n('mktg-ct-all-default-form-heading')
                }
                cta={formHeroCta}
                header={formHeroSection?.fieldHeader || ''}
                i18nData={i18nData}
                imageUrl={formHeroSection.fieldImageUrl}
                languageKey={languageKey}
                location={location}
                marketoForm={branch === 'T_B' ? alternateMarketoForm : marketoForm}
                onFormSuccess={handleOnContactFormSuccess}
                subheader={formHeroSection?.fieldSubheader || ''}
                subheaderCta={formHeroSubheaderLink}
                urlLangPrefix={urlLangPrefix}
              />
            )}
          </ABTest.Context.Consumer>
        </ABTest>
      )}

      {!isContactFormSuccessful && !shouldSkipForm ? (
        <>
          {logos && !!logos.length && (
            <Box as="section" css={theme => ({ background: theme.colors.blue[10] })} py={7}>
              <Container>
                <Text
                  css={theme => ({
                    textAlign: 'center',
                    marginBottom: theme.space.s64,
                    [mqMin.tablet]: { marginBottom: theme.space.s40 },
                  })}
                  variant="paragraphXL300"
                >
                  {logoSectionHeader || i18n('mktg-enterprise-trusted-by')}
                </Text>
                <LogoSection logos={logos} logosColor="dark" />
              </Container>
            </Box>
          )}
          {fieldProductSectionHeader && fieldProductSectionSubheader && (
            <Container>
              <Box css={{ textAlign: 'center', maxWidth: '680px', width: '100%', margin: '60px auto' }}>
                <Heading>{fieldProductSectionHeader}</Heading>
                <Text css={{ marginBottom: '60px' }} variant="paragraphM400">
                  {fieldProductSectionSubheader}
                </Text>
                <Box
                  css={theme => ({
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: theme.space.s40,
                  })}
                >
                  {PRODUCT_LOGOS.map((logoSrc, index) => (
                    <Image
                      key={`product-${index}`}
                      css={{ width: '200px', height: '23px' }}
                      imageAlt=""
                      imageSrc={logoSrc}
                    />
                  ))}
                </Box>
              </Box>
            </Container>
          )}
        </>
      ) : (
        <>
          {resourceCards && (
            <ResourceCards
              i18nData={i18nData}
              resourceCards={resourceCards}
              sectionHeading={resourceCardsSection.fieldTitle}
            />
          )}
        </>
      )}
    </Layout>
  );
};

export default ContactForm;

export const Head: HeadFC<Lucid.Template.HubPageProps['data'], Lucid.Template.HubPageProps['pageContext']> = ({
  ...props
}) => <LucidHead<Drupal_NodeContactForm> {...props} />;

export const query = graphql`
  query ($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      secondaryNodeReference: nodeById(id: "541", language: $LanguageId) {
        entityUrl {
          path
        }
      }
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeContactForm {
          ...entityFields
          fieldFormHero {
            entity {
              ... on Drupal_ParagraphFormHero {
                fieldHeader
                fieldSubheader
                fieldSubheaderLink {
                  url {
                    path
                  }
                  title
                }
                fieldImageUrl
                fieldMarketoForm {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                      fieldMarketoConfirmBody
                      fieldMarketoConfirmHeading
                      fieldMarketoConfirmCta {
                        entity {
                          ... on Drupal_ParagraphCtaLinkButton {
                            fieldDisplayAsButton
                            fieldIsSecondary
                            fieldCta {
                              url {
                                path
                              }
                              title
                            }
                          }
                        }
                      }
                    }
                  }
                }
                fieldCtaButtons {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldButtonLink
                      fieldButtonText
                      fieldIsSecondary
                    }
                  }
                }
              }
            }
          }
          fieldCarouselLogos {
            entity {
              ... on Drupal_ParagraphCarouselLogo {
                fieldHeader
                fieldLogosSection {
                  entity {
                    ... on Drupal_ParagraphExternalLogoSection {
                      fieldLogosColor
                      fieldRowHeight
                      fieldLogoLayoutVariant
                      fieldExternalLogos {
                        entity {
                          ... on Drupal_ParagraphExternalLogo {
                            fieldLogoSlug
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fieldProductSectionHeader
          fieldProductSectionSubheader
          fieldResourceCards {
            entity {
              ... on Drupal_ParagraphResourceCards {
                fieldTitle
                fieldResourceCardItems {
                  entity {
                    ... on Drupal_ParagraphResourceCard {
                      fieldHeader
                      fieldDescription
                      fieldResourceLink {
                        url {
                          path
                        }
                        title
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" }, name: { in: [$i18nKey, "en"] } }) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_consideration_faq_link_text
            mktg_enterprise_trusted_by
            mktg_generics_forms_submit
            mktg_form_page_thanks_enterprise
            mktg_form_page_thanks_body
            mktg_ct_all_default_form_heading
            mktg_ct_all_default_form_thankyou_message
            mtkg_ppc_team_language_spark
            mktg_form_page_help
            mktg_form_page_h1
            mktg_learn_more
            mktg_form_page_body_enterprise
            mktg_form_page_pricing_link
          }
        }
      }
    }
  }
`;
